import type { BrowserOptions, Breadcrumb, ErrorEvent } from "@sentry/browser";

/**
 * Crash reports to Sentry (ADR 0012, P2-AN-03). Only builds given a DSN report (the preview and
 * staging deploys; production waits for the privacy notice to list Sentry). The SDK loads after
 * first paint, so it never delays the page; errors from before that are held and sent once it's up.
 * Nothing personal: no user info, cookies, headers, bodies or local variables; no replay, tracing or
 * session pings; and no URL's query or fragment (invite secrets travel in the fragment).
 */
export interface CrashReportConfig {
  dsn: string;
  environment: string;
  release?: string | undefined;
}

/** The SDK calls this module uses, so tests can stand in for it. */
export interface CrashReporter {
  init(options: BrowserOptions): unknown;
  captureException(error: unknown): unknown;
}

/** The URL without its query and fragment. */
export function scrubUrl(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** A breadcrumb with its URLs scrubbed (fetch and xhr carry `url`, navigation `from` and `to`). */
export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const data = crumb.data && { ...crumb.data };
  if (data)
    for (const key of ["url", "from", "to"])
      if (typeof data[key] === "string") data[key] = scrubUrl(data[key]);
  return data ? { ...crumb, data } : crumb;
}

/** The event as it may leave the page: scrubbed URLs, and no request headers (the referrer). */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  const out: ErrorEvent = { ...event };
  if (event.request) {
    const { url } = event.request;
    out.request = url === undefined ? {} : { url: scrubUrl(url) };
  }
  if (event.breadcrumbs) out.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  return out;
}

/** Default integrations left out (@sentry/browser 11 names). */
const LEFT_OUT = new Set(["BrowserSession", "Console"]);

/**
 * Every data category Sentry 11 collects by default, off (`dataCollection` replaced
 * `sendDefaultPii`, and its defaults collect user info, cookies, headers and bodies).
 */
export const NOTHING_PERSONAL = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
} satisfies BrowserOptions["dataCollection"];

/** The SDK's options: errors only, with nothing about the person. */
export function sentryOptions(config: CrashReportConfig): BrowserOptions {
  return {
    dsn: config.dsn,
    environment: config.environment,
    ...(config.release === undefined ? {} : { release: config.release }),
    dataCollection: NOTHING_PERSONAL,
    // Default integrations, less the session pings and the console breadcrumbs (they could carry
    // whatever the page logged): breadcrumbs keep only clicks, navigation and requests.
    integrations: (defaults) => defaults.filter((i) => !LEFT_OUT.has(i.name)),
    beforeBreadcrumb: (crumb) => (crumb.category === "console" ? null : scrubBreadcrumb(crumb)),
    beforeSend: scrubEvent,
  };
}

/** The most errors held while the SDK loads; a page throwing more than this is one report anyway. */
const HELD_MAX = 10;

/**
 * Starts crash reports: holds errors from now on, and once `whenIdle` runs, loads the SDK, sends
 * what was held, and leaves the rest to the SDK's own handlers.
 */
export function startCrashReports(
  config: CrashReportConfig,
  load: () => Promise<CrashReporter>,
  target: EventTarget = window,
  whenIdle: (run: () => void) => void = afterFirstPaint,
): void {
  const held: unknown[] = [];
  const hold = (e: Event) => {
    if (held.length >= HELD_MAX) return;
    held.push("reason" in e ? e.reason : "error" in e ? e.error : e);
  };
  target.addEventListener("error", hold);
  target.addEventListener("unhandledrejection", hold);
  whenIdle(() => {
    load().then(
      (sentry) => {
        target.removeEventListener("error", hold);
        target.removeEventListener("unhandledrejection", hold);
        sentry.init(sentryOptions(config));
        for (const error of held.splice(0)) sentry.captureException(error);
      },
      // The SDK didn't load (offline, blocked): the page carries on without crash reports.
      () => {
        target.removeEventListener("error", hold);
        target.removeEventListener("unhandledrejection", hold);
      },
    );
  });
}

/** Runs `run` once the page has loaded and the browser is idle (or after a moment, without idle callbacks). */
function afterFirstPaint(run: () => void): void {
  const idle = () => {
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 5_000 });
    else setTimeout(run, 1_000);
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}

/** This build's crash report settings, or null when it has no DSN (local dev, production for now). */
export function crashReportConfig(env: ImportMetaEnv): CrashReportConfig | null {
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return null;
  return {
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT ?? env.MODE,
    release: env.VITE_SENTRY_RELEASE,
  };
}
